import type { ReactElement } from 'react';

import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import TelegramIcon from '@mui/icons-material/Telegram';
import { Alert, Button, Chip, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import type { UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import type { NormalizedApiError } from '@/shared/api';
import { getIntlLocale } from '@/shared/i18n';

import { availabilityReasons, capabilityDisplayState } from '../../model/availability';
import type { AvailabilityReason } from '../../model/availability';
import type {
  ChatbotAvailability,
  ChatbotChannel,
  ChatbotChannelAvailability,
} from '../../model/types';

export interface AvailabilityStripProps {
  query: UseQueryResult<ChatbotAvailability, NormalizedApiError>;
  canViewChannels: boolean;
  canViewTelegram: boolean;
  canViewMax: boolean;
  /** Deep-link target for `CHATBOT_OFF_TENANT` — design §6.1 Q3 ("Company"). */
  canViewTenant: boolean;
}

const CHANNEL_ICON: Record<ChatbotChannel, ReactElement> = {
  TELEGRAM: <TelegramIcon fontSize="small" />,
  MAX: <ChatRoundedIcon fontSize="small" />,
};

function deepLinkFor(
  reason: AvailabilityReason,
  channel: ChatbotChannel,
): { to: string; labelKey: string; allowed: (props: AvailabilityStripProps) => boolean } | null {
  switch (reason) {
    case 'TOUCHPOINT_OFF':
      return {
        to: '/settings/channels',
        labelKey: 'availability.links.channels',
        allowed: (p) => p.canViewChannels,
      };
    case 'BOT_NOT_CONFIGURED':
      return channel === 'TELEGRAM'
        ? {
            to: '/settings/telegram',
            labelKey: 'availability.links.telegram',
            allowed: (p) => p.canViewTelegram,
          }
        : { to: '/settings/max', labelKey: 'availability.links.max', allowed: (p) => p.canViewMax };
    case 'CHATBOT_OFF_TENANT':
      return {
        to: '/settings/tenant',
        labelKey: 'availability.links.company',
        allowed: (p) => p.canViewTenant,
      };
    case 'CHATBOT_OFF_PLATFORM':
      return null;
  }
}

function ChannelRow({
  channel,
  availability,
  props,
}: {
  channel: ChatbotChannelAvailability;
  availability: ChatbotAvailability;
  props: AvailabilityStripProps;
}) {
  const { t } = useTranslation('chatbot');
  const reasons = availabilityReasons(availability, channel);
  const [firstReason, ...otherReasons] = reasons;
  const channelLabel = t(`scope.${channel.channel.toLowerCase()}`);
  const statusUnknown = channel.botConfiguredKnown === false;

  // *(delta 2026-09-23)* `answering=true` while the bot connection itself could not be confirmed —
  // a neutral "Bot status unknown" chip, never "not connected" (design §17.5).
  if (channel.answering && statusUnknown) {
    const unknownChip = (
      <Chip
        size="small"
        variant="outlined"
        icon={<HelpOutlineRoundedIcon fontSize="small" />}
        label={t('availability.channel.botUnknown', { channel: channelLabel })}
      />
    );
    const link = {
      to: channel.channel === 'TELEGRAM' ? '/settings/telegram' : '/settings/max',
      allowed: channel.channel === 'TELEGRAM' ? props.canViewTelegram : props.canViewMax,
    };
    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Tooltip title={t('availability.tooltip.botUnknown', { channel: channelLabel })}>
          {unknownChip}
        </Tooltip>
        {link.allowed ? (
          <Button size="small" component={RouterLink} to={link.to}>
            {t(
              channel.channel === 'TELEGRAM'
                ? 'availability.links.telegram'
                : 'availability.links.max',
            )}
          </Button>
        ) : null}
      </Stack>
    );
  }

  const chip = channel.answering ? (
    <Chip
      size="small"
      color="success"
      icon={CHANNEL_ICON[channel.channel]}
      label={t('availability.channel.answering', { channel: channelLabel })}
    />
  ) : firstReason ? (
    <Chip
      size="small"
      color="warning"
      icon={
        firstReason === 'CHATBOT_OFF_PLATFORM' ? (
          <LockOutlinedIcon fontSize="small" />
        ) : (
          CHANNEL_ICON[channel.channel]
        )
      }
      label={t('availability.channel.notAnswering', {
        channel: channelLabel,
        reason: t(`availability.reasons.${firstReason}`),
      })}
    />
  ) : (
    <Chip
      size="small"
      color="warning"
      icon={CHANNEL_ICON[channel.channel]}
      label={t('availability.channel.notAnsweringNoReason', { channel: channelLabel })}
    />
  );

  // *(delta 2026-09-23)* `BOT_STATUS_UNKNOWN` only ever joins the "Also:" tooltip list — it is never
  // the primary reason (design §17.5).
  const tooltipReasonLabels = [
    ...otherReasons.map((r) => t(`availability.reasons.${r}`)),
    ...(statusUnknown ? [t('availability.reasons.BOT_STATUS_UNKNOWN')] : []),
  ];

  const wrappedChip =
    firstReason === 'CHATBOT_OFF_PLATFORM' ? (
      <Tooltip title={t('availability.tooltip.platformOff')}>{chip}</Tooltip>
    ) : !channel.answering && tooltipReasonLabels.length === 0 && !firstReason ? (
      <Tooltip title={t('availability.tooltip.checkConnection')}>{chip}</Tooltip>
    ) : tooltipReasonLabels.length > 0 ? (
      <Tooltip title={t('availability.otherReasons', { reasons: tooltipReasonLabels.join(', ') })}>
        {chip}
      </Tooltip>
    ) : (
      chip
    );

  const link = firstReason ? deepLinkFor(firstReason, channel.channel) : null;

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      {wrappedChip}
      {link && link.allowed(props) ? (
        <Button size="small" component={RouterLink} to={link.to}>
          {t(link.labelKey)}
        </Button>
      ) : null}
    </Stack>
  );
}

function CapabilityChip({
  labelKey,
  capability,
}: {
  labelKey: string;
  capability: { effective: boolean; lockedByPlatform?: boolean } | undefined;
}) {
  const { t } = useTranslation('chatbot');
  const state = capabilityDisplayState(capability);
  const label = t(labelKey);

  if (state === 'ON') {
    return <Chip size="small" color="success" label={`${label} · ${t('availability.state.on')}`} />;
  }
  if (state === 'LOCKED') {
    return (
      <Tooltip title={t('availability.tooltip.platformOff')}>
        <Chip
          size="small"
          variant="outlined"
          icon={<LockOutlinedIcon fontSize="small" />}
          label={`${label} · ${t('availability.state.locked')}`}
        />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={t('availability.tooltip.tenantOff')}>
      <Chip size="small" variant="outlined" label={`${label} · ${t('availability.state.off')}`} />
    </Tooltip>
  );
}

/** Read-only strip answering "is the bot answering on each messenger, and if not, why" (design §6.1). */
export function AvailabilityStrip(props: AvailabilityStripProps) {
  const { t } = useTranslation('chatbot');
  const { query } = props;

  const projectionUpdatedAt = query.data?.projectionUpdatedAt;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" flexWrap="wrap">
        <Typography
          variant="overline"
          color="text.secondary"
          component="h2"
          sx={{ display: 'block', mb: 1 }}
        >
          {t('availability.title')}
        </Typography>
        {query.isSuccess ? (
          projectionUpdatedAt ? (
            <Tooltip
              title={new Intl.DateTimeFormat(getIntlLocale(), {
                dateStyle: 'medium',
                timeStyle: 'short',
              }).format(new Date(projectionUpdatedAt))}
            >
              <Typography variant="caption" color="text.secondary">
                {t('availability.asOf', {
                  value: new Intl.DateTimeFormat(getIntlLocale(), {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  }).format(new Date(projectionUpdatedAt)),
                })}
              </Typography>
            </Tooltip>
          ) : (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: 'text.secondary' }} aria-hidden />
              <Typography variant="caption" color="text.secondary">
                {t('availability.notSynced')}
              </Typography>
            </Stack>
          )
        ) : null}
      </Stack>
      {query.isLoading ? (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1}>
            <Skeleton variant="rounded" width={140} height={24} />
            <Skeleton variant="rounded" width={160} height={24} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Skeleton variant="rounded" width={100} height={24} />
            <Skeleton variant="rounded" width={100} height={24} />
          </Stack>
        </Stack>
      ) : query.isError ? (
        <Alert
          severity="warning"
          variant="outlined"
          action={
            <Button size="small" onClick={() => void query.refetch()}>
              {t('availability.retry')}
            </Button>
          }
        >
          {t('availability.loadError')}
        </Alert>
      ) : query.data ? (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            {query.data.channels.map((channel) => (
              <ChannelRow
                key={channel.channel}
                channel={channel}
                availability={query.data!}
                props={props}
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <CapabilityChip
              labelKey="availability.capabilities.chatbot"
              capability={query.data.chatbotCapability}
            />
            <CapabilityChip
              labelKey="availability.capabilities.ai"
              capability={query.data.aiCapability}
            />
            <CapabilityChip
              labelKey="availability.capabilities.handoff"
              capability={query.data.humanHandoffCapability}
            />
          </Stack>
        </Stack>
      ) : null}
    </Paper>
  );
}
